import React from 'react';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Banner,
  Button,
  DataTable,
  EmptyState,
  EmptyStateCompareGraphic,
  EmptyStateScanGraphic,
  LoadingOrbit,
  Page,
  PageHeader,
  TagBadge,
  Text,
  type MultiSelectOption,
} from '@chatsdet/ui';
import { Camera } from '@phosphor-icons/react';
import type { VisualCheckpointListItem } from '@chatsdet/types';
import { useWorkflowsQuery } from '../../../hooks/useWorkflows';
import { useTestSuites } from '../../../features/test-suites/useTestSuites';
import { useHasVisualCheckpoints, useVisualCheckpoints } from '../../../features/visual/useVisual';
import { checkpointSubject, outcomePresentation } from '../../../features/visual/visualPresentation';
import { DEFAULT_VISUAL_STATUS, isVisualSearchFiltered, type VisualSearch } from '../../../features/visual/visualSearch';
import { formatRelativeTimeAgo } from '../../../features/workflows/useAgenticRunDetail';
import { BRAND } from '../../../brand';
import { FeatureOnboardingEmptyState } from '../../components/FeatureOnboardingEmptyState';
import { VisualFilterBar } from './VisualFilterBar';

const ONBOARDING_PROMPT =
  `Use the ${BRAND.name} CLI to set up visual checkpoints for this app. First run \`${BRAND.cliCommand} workflow guide visual-checkpoints\` and follow it. Then add checkpoints to the most important journeys and run them once to record baselines.`;
const ONBOARDING_STEPS = [
  'Add a checkpoint at each screen in a flow whose look matters.',
  'The first run saves each screenshot as that checkpoint\'s baseline.',
  'Later runs compare against the baseline and flag anything that changed.',
  'Changes show up here for you to review, mark as a regression, or accept.',
];

const COLUMNS: ColumnDef<StockFeatures, VisualCheckpointListItem>[] = [
  {
    id: 'thumbnail',
    header: '',
    enableSorting: false,
    cell: ({ row }) =>
      row.original.imageUrl ? (
        <img
          src={row.original.imageUrl}
          alt=""
          loading="lazy"
          className="h-12 w-20 rounded object-cover object-top outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
        />
      ) : (
        <div className="h-12 w-20 rounded bg-muted" />
      ),
    meta: { minWidth: 110 },
  },
  {
    id: 'checkpoint',
    header: 'Checkpoint',
    enableSorting: false,
    cell: ({ row }) => {
      const subject = checkpointSubject(row.original);
      return (
        <div className="min-w-0">
          <Text size="sm" className="font-medium truncate">
            {row.original.label}
          </Text>
          <Text size="xs" className="text-muted-foreground truncate">
            {subject.secondary ? `${subject.primary} · ${subject.secondary}` : subject.primary}
          </Text>
        </div>
      );
    },
    meta: { flex: 3, minWidth: 240 },
  },
  {
    id: 'suite',
    header: 'Suite',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm">{row.original.suiteName ?? '—'}</Text>,
    meta: { flex: 2, minWidth: 140 },
  },
  {
    id: 'environment',
    header: 'Environment',
    enableSorting: false,
    cell: ({ row }) => <Text size="sm">{row.original.environmentName}</Text>,
    meta: { flex: 1, minWidth: 120 },
  },
  {
    id: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => {
      const { label, color } = outcomePresentation(row.original.outcome);
      return <TagBadge name={label} color={color} size="md" />;
    },
    meta: { minWidth: 130 },
  },
  {
    id: 'when',
    header: 'When',
    enableSorting: false,
    cell: ({ row }) => (
      <Text size="sm" className="text-muted-foreground">
        {formatRelativeTimeAgo(row.original.createdAt)}
      </Text>
    ),
    meta: { minWidth: 110 },
  },
];

/** Options from a list query plus the currently selected value, which may not be in the list. */
function withSelected(options: MultiSelectOption[], selected: string | undefined): MultiSelectOption[] {
  return selected && !options.some((o) => o.id === selected)
    ? [...options, { id: selected, label: selected }]
    : options;
}

function distinctOptions(values: Array<string | null>): MultiSelectOption[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v)))).map((v) => ({ id: v, label: v }));
}

export function VisualPage() {
  const search = useSearch({ from: '/visual' });
  const navigate = useNavigate();
  const query = useVisualCheckpoints(search);
  const suites = useTestSuites();
  const workflows = useWorkflowsQuery({ pageSize: 100 });

  const items = React.useMemo<VisualCheckpointListItem[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data]
  );

  const hasCheckpoints = useHasVisualCheckpoints(query.isSuccess && items.length === 0);

  const updateSearch = React.useCallback(
    (updates: Partial<VisualSearch>) =>
      navigate({ to: '/visual', search: (prev: VisualSearch) => ({ ...prev, ...updates }) }),
    [navigate]
  );

  // Spec files and environment names have no list endpoint; offer what has loaded so far.
  const specOptions = withSelected(distinctOptions(items.map((i) => i.specFile)), search.spec);
  const environmentOptions = withSelected(distinctOptions(items.map((i) => i.environmentName)), search.environment);
  const suiteOptions = withSelected(
    (suites.data ?? []).map((s) => ({ id: s.id, label: s.name })),
    search.suite
  );
  const workflowOptions = withSelected(
    (workflows.data?.items ?? []).map((w) => ({ id: w.id, label: w.name })),
    search.workflow
  );

  const listEmpty = query.isSuccess && items.length === 0;
  const neverUsed = listEmpty && hasCheckpoints.isFetchedAfterMount && hasCheckpoints.data === false;
  // Empty list whose "ever used" answer has not landed since this page mounted: keep the chrome out until it does
  // (background refetches on focus keep showing the cached answer).
  const settling = listEmpty && !hasCheckpoints.isFetchedAfterMount;
  const hasFilters = isVisualSearchFiltered(search);
  const clearFilters = () => navigate({ to: '/visual', search: { status: DEFAULT_VISUAL_STATUS } });

  return (
    <Page>
      <PageHeader
        icon={<Camera size={24} weight="fill" />}
        title="Visual"
        description="Review checkpoint screenshots that changed between runs."
      />
      {!neverUsed && !settling && (
      <VisualFilterBar
        search={search}
        onChange={updateSearch}
        onClear={clearFilters}
        suiteOptions={suiteOptions}
        workflowOptions={workflowOptions}
        specOptions={specOptions}
        environmentOptions={environmentOptions}
      />
      )}
      {query.isError && !query.data ? (
        <Banner variant="error">{query.error.message}</Banner>
      ) : query.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : items.length === 0 ? (
        hasCheckpoints.isError ? (
          <Banner variant="error">{hasCheckpoints.error.message}</Banner>
        ) : settling ? (
          <div className="flex items-center justify-center py-12">
            <LoadingOrbit size="lg" />
          </div>
        ) : neverUsed ? (
          <FeatureOnboardingEmptyState
            icon={<Camera size={48} />}
            graphic={<EmptyStateCompareGraphic />}
            title="Catch unintended visual changes"
            whatItIs="Visual checkpoints screenshot your app during test runs and compare each one with the last accepted version, so you notice when a page looks different."
            steps={ONBOARDING_STEPS}
            agentPrompt={ONBOARDING_PROMPT}
          />
        ) : hasFilters ? (
          <EmptyState
            icon={<Camera size={48} />}
            title="No results for these filters"
            description="Nothing matches the current filters."
            action={
              <Button variant="outline" size="sm" onPress={clearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <EmptyState
            icon={<Camera size={48} />}
            graphic={<EmptyStateScanGraphic />}
            title="No changes since the last baseline"
            description="Every checkpoint matches its baseline. Changes will show up here."
          />
        )
      ) : (
        <>
          <DataTable
            columns={COLUMNS}
            data={items}
            aria-label="Visual checkpoints table"
            getRowId={(row) => row.id}
            getRowHref={(row) => `/visual/${row.id}`}
          />
          {query.hasNextPage && (
            <div className="flex justify-center py-4">
              <Button variant="outline" onPress={() => query.fetchNextPage()} isDisabled={query.isFetchingNextPage}>
                {query.isFetchingNextPage ? 'Loading...' : 'Load more'}
              </Button>
            </div>
          )}
        </>
      )}
    </Page>
  );
}
